import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  DatePicker,
  Button,
  Space,
  Spin,
  Tooltip,
  Typography,
  Tag,
} from "antd";
import moment from "moment";
import { useReturnOrderStore } from "./store";
import { InfoCircleOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Link, Text } = Typography;

const ReturnOrders = () => {
  const { returnOrderData, returnOrdersLoading, error, fetchReturnOrdersData } =
    useReturnOrderStore();
  const [search, setSearch] = useState("");
  const [dates, setDates] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    const formattedStartDate = dates[0] ? dates[0].startOf("day").toISOString() : "" ;
    const formattedEndDate = dates[1] ? dates[1].startOf("day").toISOString() : "";
    fetchReturnOrdersData({
      search,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      page,
      limit,
    });
  }, [search, dates, page, limit]);

  const handleReset = () => {
    setSearch("");
    setDates([]);
    setPage(1);
    setLimit(10);
  };

  const truncateComment = (comment) => {
    if (!comment) return "N/A";
    const words = comment.split(" ");
    return words.length > 4 ? (
      <Tooltip title={comment}>{words.slice(0, 3).join(" ")}...</Tooltip>
    ) : (
      comment
    );
  };

  const columns = [
    {
      title: (
        <>
          <Tooltip title="Order ID of the order that was returned">
            Order ID <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      dataIndex: "orderId",
      key: "orderId",
      align: "center",
      render: (orderId) => <Tag color="green">{orderId}</Tag>,
    },
    {
      title: (
        <>
          <Tooltip title="Name of the customer who placed the order click to view details">
            Customer <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      key: "customerName",
      align: "center",
      render: (_, record) => (
        <Link
          href={`/lead-details/${record.caseId}`}
          style={{ color: "#1890ff" }}
        >
          {record.selectedAddress?.name || "N/A"}
        </Link>
      ),
    },
    {
      title: (
        <>
          <Tooltip title="List of products in the order with details">
            Products <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      key: "products",
      render: (_, record) =>
        record.returnItems.map((item, index) => (
          <Tooltip
            key={index}
            title={
              <>
                <strong>Variant ID:</strong> {item.variant_id} <br />
                <strong> Return Quantity:</strong> {item.returnQuantity}
                <br />
                <strong> Total Quantity:</strong> {item.quantity}
              </>
            }
          >
            <div style={{ marginBottom: 8 }}>
              <strong>{item.name}</strong>{" "}
              <Tag color="red">Return Qty: {item.returnQuantity}</Tag>
              <Tag color="green">Total Qty: {item.quantity}</Tag>
            </div>
          </Tooltip>
        )),
    },
    {
      title: (
        <>
          <Tooltip title="Reason for the return and any additional comments">
            Reasons & Comments <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      key: "reasons",
      render: (_, record) => (
        <div>
          <strong>Reason:</strong>{" "}
          <Text type="warning">{record.reason || "N/A"}</Text> |{" "}
          <strong>Comment:</strong> {truncateComment(record.comments)}
        </div>
      ),
    },
    {
      title: (
        <>
          <Tooltip title="Address to which the order was delivered">
            Address <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      key: "address",
      align: "center",
      render: (_, record) => (
        <Tooltip
          title={
            <>
              <strong>Address Line 1:</strong>{" "}
              {record.selectedAddress?.address1} <br />
              <strong>Address Line 2:</strong>{" "}
              {record.selectedAddress?.address2 || "N/A"} <br />
              <strong>ZIP:</strong> {record.selectedAddress?.zip}
            </>
          }
        >
          📍 {record.selectedAddress?.city} | {record.selectedAddress?.province}
        </Tooltip>
      ),
    },
    {
      title: (
        <>
          <Tooltip title="Tracking url of the return">
            URL <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      key: "tracking",
      render: (_, record) => (
        <>
          <Tooltip title="Click to view tracking details">
            <Link
              href={record.trackingUrl || "#"}
              target="_blank"
              style={{ color: "#1890ff" }}
            >
               <Tag color="red">
              Click
              </Tag>
            </Link>
          </Tooltip>
        </>
      ),
    },
    {
      title: (
        <>
          <Tooltip title="Courier & SubCourier partner details">
            Courier  <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      key: "courier",
      render: (_, record) => (
        <>
         <Tooltip title="Courier partner">
          <Tag color="blue">{record?.courierPartner?.toUpperCase() || "N/A"}</Tag>
          </Tooltip>
          <br />
          <Tooltip title="Subcourier partner">
          <Tag color="green">
            {record?.subCourierPartner?.toUpperCase() || "N/A"}
          </Tag>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px", backgroundColor: "#f9f9f9" }}>
      <div
        style={{
          backgroundColor: "white",
          borderBottom: "1px solid #f0f0f0",
          padding: "10px",
          borderRadius: "8px",
        }}
      >
        <h2 style={{ color: "#333", fontWeight: "bold" }}>Return Orders</h2>
        <Space style={{ marginBottom: "10px" }}>
          <Input
            placeholder="Search by Order ID or Case ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 300 }}
          />
          <RangePicker
            value={dates}
            onChange={(values) => setDates(values)}
            format="YYYY-MM-DD"
          />
          <Button type="primary" onClick={() => setPage(1)}>
            Filter
          </Button>
          <Button onClick={handleReset}>Reset</Button>
        </Space>
      </div>
      {returnOrdersLoading && (
        <Spin size="large" style={{ marginTop: "20px" }} />
      )}
      {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
      <Table
        columns={columns}
        dataSource={returnOrderData.data || []}
        bordered
        pagination={{
          current: page,
          pageSize: limit,
          total: returnOrderData.meta?.total || 0,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setLimit(newPageSize);
          },
        }}
        rowKey={(record) => record._id}
        style={{
          marginTop: "20px",
          backgroundColor: "white",
          borderRadius: "8px",
        }}
        rowClassName={(record, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
    </div>
  );
};

export default ReturnOrders;
